<template lang="pug">
transition(name="slide-top-height")
  .FcwAnnouncementBanner__wrapper(v-if="show && isVisible")
    .FcwAnnouncementBanner(:style="style")
      .FcwAnnouncementBanner__contentWrapper
        FIcon(
          :name="icon"
          :color="iconColor"
          size="24px"
        )
        .FcwAnnouncementBanner__content
          h3.FcwAnnouncementBanner__title {{ title }}
          p.FcwAnnouncementBanner__description {{ description }}
      .FcwAnnouncementBanner__end
        FcwButtonLink.FcwAnnouncementBanner__cta(
          v-if="buttonLocation && buttonText"
          :location="buttonLocation"
          :color="iconColor"
        ) {{ buttonText }}
        FButton.FcwAnnouncementBanner__closeButton(
          v-if="closable"
          icon
          ghost
          size="tiny"
          :color="iconColor"
          @click="isVisible = false"
        )
          FIcon(name="close")
</template>

<style lang="stylus">
.FcwAnnouncementBanner
  display flex
  justify-content center
  align-items center
  padding rem(16) rem(16) rem(16) rem(48)
  gap rem(12)
  background var(--FcwAnnouncementBanner--backgroundColor)
  z-index 20
  width 100%
  text-wrap wrap

  +media-down('xs')
    flex-direction column
    padding rem(16) rem(24)
    justify-content space-between

.FcwAnnouncementBanner__contentWrapper
  display flex
  align-items flex-start
  gap rem(12)
  width 100%

.FcwAnnouncementBanner__closeButton
  +media-down('xs')
    position absolute
    right rem(16)
    top rem(16)

.FcwAnnouncementBanner__content
  display flex
  flex-direction column
  gap rem(4)

.FcwAnnouncementBanner__title
  font-size rem(16)
  font-weight 600
  color var(--FcwAnnouncementBanner--titleColor)
  line-height rem(24)

.FcwAnnouncementBanner__description
  font-size rem(16)

.FcwAnnouncementBanner__end
  display flex
  align-items center
  gap rem(12)

.FcwAnnouncementBanner__wrapper
  width 100%
  overflow hidden

.slide-top-height-enter-active,
.slide-top-height-leave-active
  transition max-height 300ms ease-out, opacity 200ms ease-out
  will-change max-height

.slide-top-height-enter-from,
.slide-top-height-leave-to
  opacity 0.6
  max-height 0

.slide-top-height-enter-to,
.slide-top-height-leave-from
  opacity 1
  // arbitrary max height, high enough to handle all cases
  max-height rem(200)
</style>

<script setup lang="ts">
import { getCssColor } from '@fifteen/design-system-vue';

import type { BaseColorDesignToken, Icon } from '@fifteen/design-system-vue';

export interface FcwAnnouncementBannerProps {
  /**
   * Title to display
   */
  title?: string;
  /**
   * Description to display
   */
  description?: string;
  /**
   * Icon to display close to the text
   */
  icon?: Icon;
  /**
   * Color of the alert
   */
  type?: BaseColorDesignToken;
  /**
   * Add a button to close the alert
   */
  closable?: boolean;
  /**
   * Alert visibility
   */
  isVisible?: boolean;
  /**
   * Text to display on the button
   */
  buttonText?: string;
  /**
   * Button link location
   */
  buttonLocation?: string;
}

const props = withDefaults(defineProps<FcwAnnouncementBannerProps>(), {
  title: '',
  description: '',
  icon: 'infoCircle',
  type: 'info',
  closable: true,
  isVisible: false,
  buttonText: '',
  buttonLocation: '',
});

defineEmits<{
  (name: 'update:isVisible'): void;
}>();

const isVisible = useVModelProxy({
  props,
  propName: 'isVisible',
});

const iconColor = computed(() => `${props.type}--dark-1`);

const style = computed<Style>(() => ({
  '--FcwAnnouncementBanner--backgroundColor': getCssColor(
    `${props.type}--light-2`
  ),
  '--FcwAnnouncementBanner--titleColor': getCssColor(`${props.type}--dark-1`),
}));

const show = ref(false);

onMounted(async () => {
  if (props.isVisible) {
    await nextTick();
    show.value = true;
  }
});

watch(isVisible, value => {
  if (value) {
    show.value = true;
  }
});
</script>
